/**
 * MSAL Authentication Configuration
 *
 * Configuration for Microsoft Authentication Library (MSAL) integration
 * with Microsoft Entra ID (Azure AD) for the Backup Management Web App.
 *
 * Requirements:
 * - FR-001: System MUST authenticate users via Microsoft Entra ID
 * - FR-002: System MUST support admin, operator, and viewer roles
 * - FR-063: Session timeout after 8 hours of inactivity
 * - FR-064: Maximum session duration of 24 hours
 */

import { Configuration, LogLevel, PopupRequest, RedirectRequest } from '@azure/msal-browser';

/**
 * Development mode flag - bypasses Azure AD authentication
 * Set VITE_DEV_AUTH_BYPASS=true in .env for local development without Azure AD
 */
export const isDevAuthBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

/**
 * Get required environment variable with validation
 * FR-073: Hard fail if required config is missing (unless in dev bypass mode)
 */
function getRequiredEnvVar(name: string): string {
  const value = import.meta.env[name];
  if (!value) {
    if (isDevAuthBypass) {
      return 'dev-placeholder'; // Return placeholder in dev bypass mode
    }
    throw new Error(
      `Missing required environment variable: ${name}. ` +
        'Please check your .env file and ensure all required variables are set.'
    );
  }
  return value;
}

/**
 * Get optional environment variable with fallback
 */
function getEnvVar(name: string, defaultValue: string): string {
  return import.meta.env[name] || defaultValue;
}

// Azure AD Configuration from environment
const AZURE_AD_CLIENT_ID = getRequiredEnvVar('VITE_AZURE_AD_CLIENT_ID');
const AZURE_AD_TENANT_ID = getRequiredEnvVar('VITE_AZURE_AD_TENANT_ID');
const AZURE_AD_REDIRECT_URI = getEnvVar('VITE_AZURE_AD_REDIRECT_URI', window.location.origin);

/**
 * MSAL Configuration
 * https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-initializing-client-applications
 */
// 🛑 Spec 277 FR-001a / T006 — the authority is CONFIG-DRIVEN (Constitution XXIII). Default here is
//    the single-tenant authority (safe fallback); the deployed build sets VITE_ENTRA_AUTHORITY.
//    Dev sets it to `.../common` (webapp/Dockerfile) so a personal-Microsoft-account customer
//    (FCM-provisioned, e.g. doblerdata.com) can reach sign-in — `organizations` alone excludes
//    personal accounts. This does NOT weaken isolation: the backend re-establishes tenant trust
//    per token (T007, issuer must match the token's own tid) and normalizeTenantClaim/FR-002a still
//    REFUSES the shared MSA consumer namespace (9188040d) as a tenancy key. If a given customer
//    proves to home in its own Entra directory (not the consumer namespace), `organizations` is the
//    tighter option and can replace `common` — the FR-002a backstop holds either way.
const AZURE_AD_AUTHORITY = getEnvVar(
  'VITE_ENTRA_AUTHORITY',
  `https://login.microsoftonline.com/${AZURE_AD_TENANT_ID}`
);

export const msalConfig: Configuration = {
  auth: {
    clientId: AZURE_AD_CLIENT_ID,
    authority: AZURE_AD_AUTHORITY,
    redirectUri: AZURE_AD_REDIRECT_URI,
    postLogoutRedirectUri: AZURE_AD_REDIRECT_URI,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage is more secure but doesn't persist across tabs
    // localStorage persists but is slightly less secure
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false, // Set to true for IE11 support
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return; // Don't log PII
        }

        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            return;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            return;
          case LogLevel.Info:
            if (import.meta.env.DEV) {
              console.info('[MSAL]', message);
            }
            return;
          case LogLevel.Verbose:
            if (import.meta.env.DEV) {
              console.debug('[MSAL]', message);
            }
            return;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Verbose : LogLevel.Warning,
      piiLoggingEnabled: false,
    },
    windowHashTimeout: 9000,
    iframeHashTimeout: 9000,
    loadFrameTimeout: 9000,
  },
};

/**
 * Scopes for acquiring API access token
 * These are the permissions the app requests for the backend API
 * Note: The scope was named 'default' in Azure AD (without the dot prefix)
 */
export const apiScopes = {
  // Default scope for the backend API - using the custom scope name
  default: [`api://${AZURE_AD_CLIENT_ID}/default`],

  // Specific scopes if needed
  read: [`api://${AZURE_AD_CLIENT_ID}/Backup.Read`],
  write: [`api://${AZURE_AD_CLIENT_ID}/Backup.Write`],
  admin: [`api://${AZURE_AD_CLIENT_ID}/Backup.Admin`],
};

/**
 * Scopes for acquiring Microsoft Graph access token (if needed)
 */
export const graphScopes = {
  user: ['User.Read'],
  profile: ['User.Read', 'openid', 'profile', 'email'],
};

/**
 * Login request configuration
 * Used when initiating the login flow
 *
 * NOTE: Cannot combine .default with resource-specific scopes.
 * Use Graph scopes for login, acquire API token separately.
 */
export const loginRequest: PopupRequest = {
  scopes: graphScopes.profile,
  prompt: 'select_account', // Force account selection
};

/**
 * Create a login request with optional loginHint
 * 
 * Microsoft Best Practice: Use loginHint to pre-fill the username field
 * and skip account selection when the user specifies an account.
 * https://learn.microsoft.com/en-us/entra/identity-platform/msal-js-prompt-behavior
 * 
 * @param loginHint - Optional email/UPN to pre-fill in the login form
 * @returns PopupRequest configured for the login flow
 */
export function createLoginRequest(loginHint?: string): PopupRequest {
  const request: PopupRequest = {
    scopes: graphScopes.profile,
  };

  if (loginHint && loginHint.trim()) {
    // When loginHint is provided, use 'login' prompt to allow
    // re-authentication with the specified account
    request.loginHint = loginHint.trim();
    request.prompt = 'login';
  } else {
    // When no loginHint, show account picker for user to choose
    request.prompt = 'select_account';
  }

  return request;
}

/**
 * API token request
 * Used for acquiring tokens for the backend API
 */
// 🛑 Spec 277 (owner 2026-09-12) — FCM model: request OIDC scopes and send the ID token (the
//    consumer of this request, modules/backup/hooks/useAuth, must use `.idToken`, not
//    `.accessToken`). A personal Microsoft / foreign-tenant account cannot acquire the custom
//    `api://{clientId}/…` scope (AADSTS500011); OIDC scopes work everywhere and the backend
//    validates the ID token (aud = clientId). See auth/config.ts for the full rationale.
export const apiTokenRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/**
 * Silent token request
 * Used for acquiring tokens silently (without user interaction)
 */
export const silentRequest: PopupRequest = {
  scopes: graphScopes.profile,
};

/**
 * Redirect request configuration
 * Used when redirecting to Azure AD for login
 */
export const redirectRequest: RedirectRequest = {
  scopes: graphScopes.profile,
  prompt: 'select_account',
};

/**
 * Token refresh configuration
 * FR-063: Session timeout after 8 hours of inactivity
 */
export const tokenConfig = {
  // Refresh token before it expires (5 minutes before)
  tokenRefreshOffsetSeconds: 300,

  // Maximum session duration in hours (FR-064)
  maxSessionDurationHours: 24,

  // Inactivity timeout in hours (FR-063)
  inactivityTimeoutHours: 8,

  // Check interval for session validity
  sessionCheckIntervalMs: 60000, // 1 minute
};

/**
 * Account selection configuration
 */
export const accountSelectionConfig = {
  // If user has multiple accounts, which one to use
  homeAccountId: undefined as string | undefined,

  // Preferred username for account selection
  username: undefined as string | undefined,
};

/**
 * Get the API base URL from environment
 */
export function getApiBaseUrl(): string {
  return getEnvVar('VITE_API_BASE_URL', '');
}

/**
 * Validate required configuration on app initialization
 */
export function validateConfig(): void {
  // Skip validation in dev bypass mode
  if (isDevAuthBypass) {
    console.warn('[Auth] Development auth bypass mode enabled - authentication is disabled');
    return;
  }

  const requiredVars = ['VITE_AZURE_AD_CLIENT_ID', 'VITE_AZURE_AD_TENANT_ID'];

  const missing = requiredVars.filter((name) => !import.meta.env[name]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missing.join(', ')}. ` +
        'Please create a .env file with the required configuration.'
    );
  }
}

export default msalConfig;
